import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  QueryList,
  ViewChildren,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

/** Enters a verification code, one digit per cube. Also usable with `ngModel` */
@Component({
  selector: "code-cubes",
  templateUrl: "code-cubes.component.html",
  styleUrls: ["code-cubes.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CodeCubesComponent),
      multi: true,
    },
  ],
})
export class CodeCubesComponent implements ControlValueAccessor, OnChanges {
  @Input() length = 6;
  @Input() disabled = false;
  @Output() codeChange = new EventEmitter<string>();
  @Output() completed = new EventEmitter<string>();
  @ViewChildren("cube") cubeRefs: QueryList<ElementRef>;

  digits: string[] = ["", "", "", "", "", ""];
  indexes: number[] = [0, 1, 2, 3, 4, 5];
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  get code(): string {
    return this.digits.join("");
  }

  ngOnChanges() {
    this.indexes = Array.from({ length: this.length }, (_, i) => i);
    this.digits = this.indexes.map((i) => this.digits[i] || "");
  }

  writeValue(value: string) {
    const chars = this.onlyDigits(value).split("");
    this.digits = this.indexes.map((i) => chars[i] || "");
  }

  registerOnChange(fn: (value: string) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  onInput(index: number, event: any) {
    const typed = this.onlyDigits(event.target.value);
    if (!typed) {
      this.digits[index] = "";
      event.target.value = "";
      this.emit();
      return;
    }
    this.fill(typed, index);
  }

  onPaste(index: number, event: any) {
    event.preventDefault();
    const clipboard = event.clipboardData || (window as any).clipboardData;
    const pasted = this.onlyDigits(clipboard ? clipboard.getData("text") : "");
    if (pasted) {
      this.fill(pasted, 0);
    }
  }

  onKeyDown(index: number, event: any) {
    switch (event.key) {
      case "Backspace":
        if (!this.digits[index] && index > 0) {
          event.preventDefault();
          this.digits[index - 1] = "";
          this.syncInputs();
          this.focus(index - 1);
          this.emit();
        }
        break;
      case "ArrowLeft":
        // the cubes are always LTR
        event.preventDefault();
        this.focus(index - 1);
        break;
      case "ArrowRight":
        event.preventDefault();
        this.focus(index + 1);
        break;
    }
  }

  onBlur() {
    this.onTouched();
  }

  private fill(text: string, from: number) {
    const chars = text.split("");
    chars.forEach((char, i) => {
      if (from + i < this.length) {
        this.digits[from + i] = char;
      }
    });
    this.syncInputs();
    this.focus(Math.min(from + chars.length, this.length - 1));
    this.emit();
  }

  private emit() {
    const code = this.code;
    this.codeChange.emit(code);
    this.onChange(code);
    if (code.length === this.length) {
      this.completed.emit(code);
    }
  }

  private syncInputs() {
    if (this.cubeRefs) {
      this.cubeRefs.forEach((ref, i) => {
        ref.nativeElement.value = this.digits[i] || "";
      });
    }
  }

  private focus(index: number) {
    const ref = this.cubeRefs && this.cubeRefs.toArray()[index];
    if (ref) {
      ref.nativeElement.focus();
      ref.nativeElement.select();
    }
  }

  private onlyDigits(text: string): string {
    return (text || "").replace(/\D/g, "");
  }
}



// WEBPACK FOOTER //
// ./src/app/common/code-cubes/code-cubes.component.ts